"use client";

import Image from "next/image";
import React from "react";
import { motion, easeOut } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Link from "next/link";

const DiscoverSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: easeOut,
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: easeOut,
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.3,
        repeat: Infinity,
        repeatType: "reverse" as const,
      },
    },
    tap: { scale: 0.95 },
  };

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      className="p-[30px] sm:p-10 2xl:py-[123px] 2xl:px-[260px] bg-[url('/imgs/discover-bg.png')] bg-cover bg-center bg-no-repeat overflow-hidden"
    >
      <motion.div
        variants={containerVariants}
        className="grid grid-cols-1 xl:grid-cols-2 gap-[78px]"
      >
        <div className="space-y-[15px] sm:space-y-[50px]">
          <motion.div
            variants={containerVariants}
            className="space-y-2.5 sm:space-y-5"
          >
            <motion.h1
              variants={itemVariants}
              className="max-w-[545px] w-full text-[35px] sm:text-[55px] leading-[86%] font-bold font-amiri text-old-lace"
            >
              The Soul of Our Flavors
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-base leading-[22px] text-white"
            >
              Behind Almajlis is Aishan, whose passion for authentic Arabian
              cuisine inspires every dish. From hand-crafted kebabs to
              slow-cooked mandi, he ensures each meal captures the rich flavors
              and warmth of Arabian tradition.
            </motion.p>

            <motion.div variants={itemVariants}>
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                className="max-w-[209px] w-full h-[58px] relative isolate center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={209}
                  height={58}
                  viewBox="0 0 209 58"
                  fill="none"
                  className="absolute inset-0 z-[-2]"
                >
                  <path
                    d="M5.3919 0.583527H203.608V21.9235L209 29.0835L203.608 36.2435V57.5835H5.3919V36.2435L0 29.0835L5.3919 21.9235V0.583527ZM6.44624 1.58353V22.2435L1.29473 29.0835L6.44624 35.9235V56.5835H202.554V35.9235L207.705 29.0835L202.554 22.2435V1.58353H6.44624Z"
                    fill="#FDF2DE"
                  />
                  <path
                    d="M0.121094 4.58353H206.772V22.2435L201.62 29.0835L206.772 35.9235V53.5835H0.121094V35.9235L5.27261 29.0835L0.121094 22.2435V4.58353ZM1.17544 5.58353V21.9235L6.56734 29.0835L1.17544 36.2435V52.5835H205.718V36.2435L200.326 29.0835L205.718 21.9235V5.58353H1.17544Z"
                    fill="#FDF2DE"
                  />
                </svg>
                <span className="text-sm leading-[22px] uppercase">
                  <Link href="/gallery"> Discover our story</Link>
                </span>
              </motion.button>
            </motion.div>
          </motion.div>

          <motion.div
            variants={imageVariants}
            className="max-w-[630px] w-full xl:h-[420px] "
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="relative"
            >
              <Image
                src={"/imgs/discover-img-3.png"}
                alt="discover-img-2"
                width={630}
                height={420}
                className="xl:hidden block object-cover mb-[15px] "
              />
              <div className="left-0 pl-5 xl:hidden block py-2 bottom-0 absolute bg-black/35 h-full w-full ">
                <div className="mt-[40%]">
                  <h2 className="text-white font-bold text-lg leading-4">
                    Abdul Aziz Aishan
                  </h2>
                  <h4 className="text-[#FDD835] font-medium text-sm leading-6">
                    Managing Director
                  </h4>
                  <p className="text-[#FDF2DE] text-[14px]">
                    Almajlis Arabian Restaurant
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.7, ease: easeOut }}
            >
              <Image
                src={"/imgs/experience-4.jpeg"}
                alt="discover-img-2"
                width={630}
                height={420}
                className="size-full object-cover"
              />
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          variants={imageVariants}
          initial={{ opacity: 0, x: 50 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.9, ease: easeOut }}
        >
          <div className="xl:block hidden max-w/[500px] w-full h-[726px] relative">
            <Image
              src={"/imgs/discover-img-1.png"}
              alt="discover-img-1"
              width={500}
              height={726}
              className=" object-cover"
            />
            <div className="left-0 pl-5 xl:block hidden  py-2 bottom-0 absolute bg-black/35 h-full w-full ">
              <div className="mt-[94%]">
                <h2 className="text-white font-bold text-[26px] leading-9">
                  Abdul Aziz Aishan
                </h2>
                <h4 className="text-[#FDD835] font-medium text-[20px] leading-6">
                  Managing Director
                </h4>
                <p className="text-[#FDF2DE] text-[17px] mt-1">
                  Almajlis Arabian Restaurant
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.section>
  );
};

export default DiscoverSection;
